import keycloak from '../config/keycloak';
import { apiTokenManager } from './ApiTokenManager';

// Event types for auth lifecycle
export type AuthEvent = 
  | 'auth:refresh-start'
  | 'auth:refresh-success' 
  | 'auth:refresh-failed'
  | 'auth:expired'
  | 'network:error'
  | 'session:logout'
  | 'session:timeout'
  | 'api-token:refresh-start'
  | 'api-token:refresh-success'
  | 'api-token:refresh-failed';

// Request queue item
interface QueuedRequest {
  id: string;
  url: string;
  options: RequestInit;
  resolve: (response: Response) => void;
  reject: (error: Error) => void;
  retryCount: number;
}

// Auth event data
interface AuthEventData {
  type: AuthEvent;
  message?: string;
  error?: Error;
  tokenValid?: boolean;
}

class SessionNetworkManager {
  private requestQueue: QueuedRequest[] = [];
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private eventListeners: Map<AuthEvent, ((data: AuthEventData) => void)[]> = new Map();
  private refreshTimer: NodeJS.Timeout | null = null;

  // Retry tracking for token refresh
  private consecutiveRefreshFailures = 0;
  private readonly MAX_REFRESH_RETRIES = 3;

  // Inactivity detection
  private lastActivity: number = Date.now();
  private inactivityTimer: NodeJS.Timeout | null = null;
  private inactivityTimeout: number = 30 * 60 * 1000; // 30 minutes
  private activityCleanup: (() => void) | null = null;

  // Network online/offline detection
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;

  constructor() {
    this.setupProactiveRefresh();
    this.setupKeycloakEventListeners();
    this.setupInactivityDetection();
    this.setupVisibilityHandler();
    this.setupOnlineOfflineDetection();
  }

  // ============================================
  // Event System
  // ============================================

  on(event: AuthEvent, callback: (data: AuthEventData) => void) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  off(event: AuthEvent, callback: (data: AuthEventData) => void) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: AuthEvent, data: Partial<AuthEventData> = {}) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const eventData: AuthEventData = { type: event, ...data };
      listeners.forEach(callback => {
        try {
          callback(eventData);
        } catch (error) {
          console.error(`Error in auth event listener for ${event}:`, error);
        }
      });
    }
  }

  // ============================================
  // Inactivity Detection
  // ============================================

  private setupInactivityDetection() {
    // Track user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      this.updateActivity();
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start inactivity monitoring
    this.startInactivityTimer();

    // Store cleanup function
    this.activityCleanup = () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }

  private updateActivity() {
    this.lastActivity = Date.now();
    
    // Reset inactivity timer
    this.startInactivityTimer();
  }

  private startInactivityTimer() {
    // Clear existing timer
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Only monitor if user is authenticated
    if (!keycloak.authenticated) {
      return;
    }

    this.inactivityTimer = setTimeout(() => {
      this.checkInactivity();
    }, 60000); // Check every minute
  }

  private checkInactivity() {
    if (!keycloak.authenticated) {
      return;
    }

    const now = Date.now();
    const timeSinceActivity = now - this.lastActivity;

    // Trigger timeout after prolonged inactivity (30 minutes)
    if (timeSinceActivity >= this.inactivityTimeout) {
      console.log('🕐 Session timeout due to inactivity (30+ minutes)');
      this.handleInactivityTimeout();
      return;
    }

    // Continue monitoring
    this.startInactivityTimer();
  }

  private handleInactivityTimeout() {
    console.log('🚪 Handling inactivity timeout...');
    
    this.emit('session:timeout', {
      message: 'Session timed out due to inactivity. Please log in again.'
    });

    // Force logout
    this.forceLogout();
  }

  private forceLogout() {
    console.log('🚪 Force logout due to inactivity...');
    
    this.cleanup();
    
    try {
      keycloak.logout();
    } catch (error) {
      console.error('Error during force logout:', error);
      // Fallback: clear tokens and redirect
      keycloak.clearToken();
      window.location.href = '/';
    }
  }

  // Refresh token when tab becomes visible after being hidden
  private setupVisibilityHandler() {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && keycloak.authenticated) {
        this.checkAndRefreshToken();
      }
    });
  }

  // Detect WiFi / network disconnect and reconnect
  private setupOnlineOfflineDetection() {
    if (typeof window === 'undefined') return;

    window.addEventListener('offline', () => {
      console.log('🌐 Network went offline');
      this.isOnline = false;
      // Reset failure counter so transient offline errors don't accumulate
      // toward the MAX_REFRESH_RETRIES limit
      this.consecutiveRefreshFailures = 0;
    });

    window.addEventListener('online', () => {
      console.log('🌐 Network back online — refreshing token');
      this.isOnline = true;
      // Reset failure counter that may have accumulated while offline
      this.consecutiveRefreshFailures = 0;
      // Proactively refresh token now that we're back online
      if (keycloak.authenticated) {
        this.refreshToken();
      }
    });
  }

  // Public method to reset activity (can be called manually)
  resetActivity() {
    this.updateActivity();
  }

  // ============================================
  // Token Management
  // ============================================

  private setupKeycloakEventListeners() {
    // Set Keycloak event handlers directly — these properties are undefined
    // initially, so checking them with `if` would skip assignment.
    keycloak.onTokenExpired = () => {
      console.log('🔐 Token expired, triggering refresh...');
      this.refreshToken();
    };

    keycloak.onAuthLogout = () => {
      console.log('🔐 User logged out');
      this.cleanup();
      this.emit('session:logout');
    };
  }

  private setupProactiveRefresh() {
    // Check token validity every 2 minutes (less frequent)
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken();
    }, 120000);
  }

  private checkAndRefreshToken() {
    if (!keycloak.authenticated || !keycloak.token) return;

    try {
      // Check if token expires in next 5 minutes (300 seconds)
      const minValidity = 300;
      
      if (keycloak.isTokenExpired(minValidity)) {
        console.log('🔐 Token expires soon, proactively refreshing...');
        this.refreshToken();
      }
    } catch (error) {
      console.error('Error checking token validity:', error);
    }
  }

  async refreshApiToken(): Promise<boolean> {
    try {
      console.log('🔄 Refreshing API token...');
      this.emit('api-token:refresh-start');

      const success = await apiTokenManager.refresh();
      
      if (success) {
        console.log('✅ API token refreshed successfully');
        this.emit('api-token:refresh-success', { tokenValid: true });
        return true;
      } else {
        console.error('❌ API token refresh failed');
        this.emit('api-token:refresh-failed');
        return false;
      }
    } catch (error) {
      console.error('❌ API token refresh error:', error);
      this.emit('api-token:refresh-failed', { error: error as Error });
      return false;
    }
  }

  async refreshToken(): Promise<boolean> {
    // Prevent multiple concurrent refreshes
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.emit('auth:refresh-start');

    this.refreshPromise = new Promise(async (resolve) => {
      try {
        console.log('🔄 Refreshing Keycloak token...');

        // Use Keycloak's built-in refresh with 5 minute minimum validity
        const refreshed = await keycloak.updateToken(300);

        if (refreshed) {
          console.log('✅ Keycloak token refreshed successfully');
          this.consecutiveRefreshFailures = 0; // Reset failure counter on success
          this.emit('auth:refresh-success', { tokenValid: true });

          // Replay queued requests
          await this.replayQueuedRequests();
          resolve(true);
        } else {
          console.log('ℹ️ Keycloak token still valid, no refresh needed');
          this.consecutiveRefreshFailures = 0;
          resolve(true);
        }
      } catch (error) {
        console.error('❌ Keycloak token refresh failed:', error);
        this.consecutiveRefreshFailures++;
        this.emit('auth:refresh-failed', { error: error as Error });

        const isDefinitelyExpired = error instanceof Error && (
          error.message.includes('invalid_grant') ||
          error.message.includes('token_expired')
        );

        if (isDefinitelyExpired) {
          // Refresh token is definitely invalid — no point retrying
          console.log('🚪 Refresh token is invalid, ending session');
          this.handleSessionExpiry();
        } else if (!this.isOnline) {
          // Network is offline — don't count toward session expiry.
          // The 'online' event handler will trigger a fresh refresh.
          console.log('🌐 Offline — skipping retry, will refresh when network returns');
          this.consecutiveRefreshFailures = 0;
        } else if (this.consecutiveRefreshFailures >= this.MAX_REFRESH_RETRIES) {
          // Exhausted retries on transient errors — give up
          console.log(`🚪 Token refresh failed ${this.consecutiveRefreshFailures} times, ending session`);
          this.handleSessionExpiry();
        } else {
          // Transient error — schedule a retry with exponential backoff
          const delayMs = Math.min(2000 * Math.pow(2, this.consecutiveRefreshFailures - 1), 30000);
          console.log(`⏳ Transient refresh error, retrying in ${delayMs}ms (attempt ${this.consecutiveRefreshFailures}/${this.MAX_REFRESH_RETRIES})`);
          setTimeout(() => this.refreshToken(), delayMs);
        }

        resolve(false);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    });

    return this.refreshPromise;
  }

  private handleSessionExpiry() {
    this.consecutiveRefreshFailures = 0;
    this.emit('auth:expired', { message: 'Session expired. Please log in again.' });

    // Clear queued requests
    this.requestQueue.forEach(req => {
      req.reject(new Error('Session expired'));
    });
    this.requestQueue = [];

    // Emit event for UI to show modal — do NOT immediately call keycloak.logout()
    // or redirect. Let SessionManager component handle the UX gracefully.
    // The SessionTimeoutModal will provide a "Sign In Again" button.
    console.log('🔐 Session expired — waiting for user action via SessionTimeoutModal');
  }

  // ============================================
  // Request Queue & Replay
  // ============================================

  private async replayQueuedRequests() {
    if (this.requestQueue.length === 0) return;

    console.log(`🔄 Replaying ${this.requestQueue.length} queued requests...`);
    
    const requests = [...this.requestQueue];
    this.requestQueue = [];

    for (const request of requests) {
      try {
        // Add fresh token to request
        const updatedOptions = await this.addAuthHeader(request.options);
        const response = await fetch(request.url, updatedOptions);
        request.resolve(response);
      } catch (error) {
        request.reject(error as Error);
      }
    }
  }

  private queueRequest(url: string, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = Date.now().toString() + Math.random().toString(36);
      
      this.requestQueue.push({
        id: requestId,
        url,
        options,
        resolve,
        reject,
        retryCount: 0
      });

      console.log(`📋 Request queued: ${options.method || 'GET'} ${url}`);
    });
  }

  // ============================================
  // Network Layer
  // ============================================

  private async addAuthHeader(options: RequestInit = {}): Promise<RequestInit> {
    const headers = new Headers(options.headers);
    
    // Try to use API token first if available
    const apiToken = await apiTokenManager.ensureValidToken();
    if (apiToken) {
      headers.set('Authorization', `Bearer ${apiToken}`);
      console.log('🔑 Using API token for request');
    }
    // Fall back to Keycloak token
    else if (keycloak.token) {
      headers.set('Authorization', `Bearer ${keycloak.token}`);
      console.log('🔑 Using Keycloak token for request');
    } else {
      console.warn('⚠️ No authentication token available');
    }

    return {
      ...options,
      headers
    };
  }

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Add auth header to request
    const requestOptions = await this.addAuthHeader(options);

    try {
      const response = await fetch(url, requestOptions);

      // Handle 401 errors
      if (response.status === 401) {
        console.log('🔐 Received 401, attempting token refresh...');
        
        // If already refreshing, queue this request
        if (this.isRefreshing) {
          console.log('📋 Token refresh in progress, queueing request...');
          return this.queueRequest(url, options);
        }

        // Try to refresh API token first
        const apiTokenRefreshed = await this.refreshApiToken();
        
        if (apiTokenRefreshed) {
          // Retry the original request with new API token
          console.log('🔄 Retrying request with fresh API token...');
          const retryOptions = await this.addAuthHeader(options);
          return fetch(url, retryOptions);
        }
        
        // Fall back to Keycloak token refresh
        const keycloakRefreshed = await this.refreshToken();
        
        if (keycloakRefreshed) {
          // Retry the original request with new Keycloak token
          console.log('🔄 Retrying request with fresh Keycloak token...');
          const retryOptions = await this.addAuthHeader(options);
          return fetch(url, retryOptions);
        }
        
        // Both refresh attempts failed, return original 401 response
        return response;
      }

      return response;
    } catch (error) {
      console.error('Network error:', error);
      this.emit('network:error', { error: error as Error });
      throw error;
    }
  }

  // ============================================
  // Utility Methods
  // ============================================

  isAuthenticated(): boolean {
    return apiTokenManager.isAuthenticated() || keycloak.authenticated || false;
  }

  getToken(): string | undefined {
    // Prefer API token if available
    const apiToken = apiTokenManager.getAccessToken();
    if (apiToken) {
      return apiToken;
    }
    return keycloak.token;
  }

  getUserId(): string | undefined {
    return keycloak.tokenParsed?.sub;
  }

  getTokenInfo() {
    const apiTokenInfo = apiTokenManager.getTokenInfo();
    const keycloakTokenInfo = this.getKeycloakTokenInfo();

    return {
      apiToken: apiTokenInfo,
      keycloakToken: keycloakTokenInfo,
      isAuthenticated: this.isAuthenticated()
    };
  }

  private getKeycloakTokenInfo() {
    if (!keycloak.token || !keycloak.tokenParsed) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    const exp = keycloak.tokenParsed.exp || 0;
    const timeUntilExpiry = exp - now;

    return {
      token: keycloak.token,
      userId: keycloak.tokenParsed.sub,
      email: keycloak.tokenParsed.email,
      expiresAt: exp,
      expiresIn: timeUntilExpiry,
      isExpired: timeUntilExpiry <= 0,
      willExpireSoon: timeUntilExpiry <= 300 // 5 minutes
    };
  }

  // ============================================
  // Cleanup
  // ============================================

  cleanup() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.activityCleanup) {
      this.activityCleanup();
      this.activityCleanup = null;
    }
    
    // Clear queued requests
    this.requestQueue.forEach(req => {
      req.reject(new Error('Session manager cleanup'));
    });
    this.requestQueue = [];
    
    this.isRefreshing = false;
    this.refreshPromise = null;

    // Clear API tokens
    apiTokenManager.logout();
  }

  // ============================================
  // Debug Methods
  // ============================================

  getDebugInfo() {
    return {
      isRefreshing: this.isRefreshing,
      queueLength: this.requestQueue.length,
      tokenInfo: this.getTokenInfo(),
      eventListeners: Array.from(this.eventListeners.keys())
    };
  }
}

// Create singleton instance
export const sessionNetworkManager = new SessionNetworkManager();

// Export for debugging
(window as any).sessionNetworkManager = sessionNetworkManager;

export default sessionNetworkManager;
